/**
 * 股票資料轉換工具
 * 統一處理各種 API 資料格式轉換為系統內部 Stock 格式
 */

import { Stock } from '../types';
import { logger } from './logger';
import { ErrorHandler } from './errorHandler';

// TWSE API 相關介面
export interface TwseCurrentPrice {
  stockCode: string;
  stockName: string;
  volume: number;
  transaction: number;
  turnover: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  closePrice: number;
  change: number;
  changePercent: number;
}

export interface TwseStockData {
  date: string;
  stockCode: string;
  stockName: string;
  volume: number;
  turnover: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  closePrice: number;
  change: number;
  transaction: number;
}

export interface RealtimeStockPrice {
  symbol: string;
  name: string;
  fullName: string;
  currentPrice: number;
  previousClose: number;
  openPrice: number;
  highPrice: number;
  lowPrice: number;
  volume: number;
  turnover: number;
  change: number;
  changePercent: number;
  updateTime: string;
  date: string;
  isMarketOpen: boolean;
  bidPrices: number[];
  askPrices: number[];
  bidVolumes: number[];
  askVolumes: number[];
  upperLimit: number;
  lowerLimit: number;
  totalVolume: number;
  transactionCount: number;
}

/**
 * 股票資料轉換器
 */
export class StockDataTransformer {
  /**
   * 安全地轉換數字
   */
  private static safeNumber(value: any, defaultValue: number = 0): number {
    if (value === null || value === undefined || value === '') {
      return defaultValue;
    }
    const num = Number(value);
    return isNaN(num) ? defaultValue : num;
  }

  /**
   * 安全地轉換字串
   */
  private static safeString(value: any, defaultValue: string = ''): string {
    if (value === null || value === undefined) {
      return defaultValue;
    }
    return String(value).trim();
  }

  /**
   * 計算漲跌幅度
   */
  private static calculateChangePercent(currentPrice: number, change: number): number {
    if (currentPrice === 0 || change === 0) return 0;
    const previousPrice = currentPrice - change;
    if (previousPrice === 0) return 0;
    return Number(((change / previousPrice) * 100).toFixed(2));
  }

  /**
   * 將 TWSE Current Price 資料轉換為 Stock 格式
   */
  static transformTwseCurrentPriceToStock(twseData: TwseCurrentPrice): Stock | null {
    try {
      const symbol = this.safeString(twseData.stockCode);
      const name = this.safeString(twseData.stockName);
      const currentPrice = this.safeNumber(twseData.closePrice);
      const change = this.safeNumber(twseData.change);
      
      if (!symbol) {
        logger.api.warn('轉換失敗：缺少股票代號', { twseData });
        return null;
      }

      const changePercent = twseData.changePercent || this.calculateChangePercent(currentPrice, change);

      return {
        symbol,
        name,
        currentPrice: Number(currentPrice.toFixed(2)),
        change: Number(change.toFixed(2)),
        changePercent: Number(changePercent.toFixed(2)),
        volume: this.safeNumber(twseData.volume),
        marketCap: 0, // TWSE API 不提供市值
        lastUpdated: new Date(),
        previousClose: Number((currentPrice - change).toFixed(2)), // 收盤資料：昨收 = 收盤價 - 漲跌
        priceAsOf: 'close' as const,
        // TWSE 特有欄位
        stockCode: symbol,
        openPrice: this.safeNumber(twseData.openPrice),
        highPrice: this.safeNumber(twseData.highPrice),
        lowPrice: this.safeNumber(twseData.lowPrice),
        transaction: this.safeNumber(twseData.transaction),
        turnover: this.safeNumber(twseData.turnover),
        dataSource: 'twse' as const,
      };
    } catch (error) {
      logger.api.error('TWSE Current Price 轉換失敗', error);
      return null;
    }
  }

  /**
   * 將 TWSE Stock Data 轉換為 Stock 格式
   */
  static transformTwseStockDataToStock(twseData: TwseStockData): Stock | null {
    try {
      const symbol = this.safeString(twseData.stockCode);
      const name = this.safeString(twseData.stockName);
      const currentPrice = this.safeNumber(twseData.closePrice);
      const change = this.safeNumber(twseData.change);
      
      if (!symbol) {
        logger.api.warn('轉換失敗：缺少股票代號', { twseData });
        return null;
      }

      const changePercent = this.calculateChangePercent(currentPrice, change);

      return {
        symbol,
        name,
        currentPrice: Number(currentPrice.toFixed(2)),
        change: Number(change.toFixed(2)),
        changePercent,
        volume: this.safeNumber(twseData.volume),
        marketCap: 0, // TWSE API 不提供市值
        lastUpdated: new Date(),
        previousClose: Number((currentPrice - change).toFixed(2)), // 收盤資料：昨收 = 收盤價 - 漲跌
        priceAsOf: 'close' as const,
        // TWSE 特有欄位
        stockCode: symbol,
        openPrice: this.safeNumber(twseData.openPrice),
        highPrice: this.safeNumber(twseData.highPrice),
        lowPrice: this.safeNumber(twseData.lowPrice),
        transaction: this.safeNumber(twseData.transaction),
        turnover: this.safeNumber(twseData.turnover),
        dataSource: 'twse' as const,
      };
    } catch (error) {
      logger.api.error('TWSE Stock Data 轉換失敗', error);
      return null;
    }
  }

  /**
   * 將即時股價資料轉換為 Stock 格式
   */
  static transformRealtimeStockPriceToStock(realtimeData: RealtimeStockPrice): Stock | null {
    try {
      const symbol = this.safeString(realtimeData.symbol);
      const name = this.safeString(realtimeData.name || realtimeData.fullName);
      
      if (!symbol) {
        logger.api.warn('轉換失敗：缺少股票代號', { realtimeData });
        return null;
      }

      return {
        symbol,
        name,
        currentPrice: Number(this.safeNumber(realtimeData.currentPrice).toFixed(2)),
        change: Number(this.safeNumber(realtimeData.change).toFixed(2)),
        changePercent: Number(this.safeNumber(realtimeData.changePercent).toFixed(2)),
        volume: this.safeNumber(realtimeData.volume),
        marketCap: 0, // MIS API 沒有市值資訊
        lastUpdated: new Date(),
        previousClose: Number(this.safeNumber(realtimeData.previousClose).toFixed(2)), // MIS y 欄位：昨日收盤價
        priceAsOf: 'realtime' as const,
        // TWSE 特有欄位
        stockCode: symbol,
        openPrice: this.safeNumber(realtimeData.openPrice),
        highPrice: this.safeNumber(realtimeData.highPrice),
        lowPrice: this.safeNumber(realtimeData.lowPrice),
        transaction: this.safeNumber(realtimeData.transactionCount),
        turnover: this.safeNumber(realtimeData.turnover),
        dataSource: 'twse' as const
      };
    } catch (error) {
      logger.api.error('即時股價資料轉換失敗', error);
      return null;
    }
  }

  /**
   * 通用的資料轉換方法 - 自動檢測資料類型並轉換
   */
  static transformToStock(data: any): Stock | null {
    if (!data) return null;

    try {
      // 檢測資料類型並使用對應的轉換方法
      if (data.stockCode || data.symbol) {
        // 檢查是否為即時資料（有 isMarketOpen 欄位）
        if ('isMarketOpen' in data || 'bidPrices' in data) {
          return this.transformRealtimeStockPriceToStock(data as RealtimeStockPrice);
        }
        
        // 檢查是否為 TWSE Current Price 格式
        if ('closePrice' in data || 'currentPrice' in data) {
          return this.transformTwseCurrentPriceToStock(data as TwseCurrentPrice);
        }
        
        // 檢查是否為 TWSE Stock Data 格式
        if ('date' in data) {
          return this.transformTwseStockDataToStock(data as TwseStockData);
        }
      }

      logger.api.warn('無法識別的資料格式', { data });
      return null;
    } catch (error) {
      const appError = ErrorHandler.handleError(error, 'StockDataTransformer.transformToStock');
      logger.api.error('資料轉換失敗', appError);
      return null;
    }
  }

  /**
   * 批量轉換股票資料
   */
  static transformBatchToStocks(dataArray: any[]): Stock[] {
    if (!Array.isArray(dataArray)) {
      logger.api.warn('批量轉換失敗：輸入不是陣列');
      return [];
    }

    const results: Stock[] = [];
    const errors: any[] = [];

    dataArray.forEach((data, index) => {
      try {
        const stock = this.transformToStock(data);
        if (stock) {
          results.push(stock);
        } else {
          errors.push({ index, data, error: '轉換返回 null' });
        }
      } catch (error) {
        errors.push({ index, data, error: error instanceof Error ? error.message : String(error) });
      }
    });

    if (errors.length > 0) {
      logger.api.warn(`批量轉換完成，有 ${errors.length} 筆失敗`, { 
        totalInput: dataArray.length, 
        successCount: results.length, 
        errorCount: errors.length 
      });
    } else {
      logger.api.info(`批量轉換成功`, { 
        totalInput: dataArray.length, 
        successCount: results.length 
      });
    }

    return results;
  }

  /**
   * 驗證 Stock 資料的完整性
   */
  static validateStock(stock: Stock): boolean {
    const requiredFields = ['symbol', 'name', 'currentPrice'];
    
    for (const field of requiredFields) {
      if (!(field in stock) || (stock as any)[field] === null || (stock as any)[field] === undefined) {
        logger.api.warn(`Stock 驗證失敗：缺少欄位 ${field}`, { stock });
        return false;
      }
    }

    // 驗證數值欄位
    if (typeof stock.currentPrice !== 'number' || stock.currentPrice < 0) {
      logger.api.warn('Stock 驗證失敗：無效的股價', { stock });
      return false;
    }

    return true;
  }

  /**
   * 清理和標準化 Stock 資料
   */
  static normalizeStock(stock: Stock): Stock {
    return {
      ...stock,
      currentPrice: Number(stock.currentPrice.toFixed(2)),
      change: Number((stock.change || 0).toFixed(2)),
      changePercent: Number((stock.changePercent || 0).toFixed(2)),
      volume: Math.max(0, stock.volume || 0),
      marketCap: Math.max(0, stock.marketCap || 0),
      lastUpdated: new Date(stock.lastUpdated || Date.now())
    };
  }
}

// 便利的導出函數
export const transformTwseToStock = StockDataTransformer.transformTwseCurrentPriceToStock;
export const transformRealtimeToStock = StockDataTransformer.transformRealtimeStockPriceToStock;
export const transformBatchToStocks = StockDataTransformer.transformBatchToStocks;
export const validateStock = StockDataTransformer.validateStock;
export const normalizeStock = StockDataTransformer.normalizeStock;
